import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Avatar,
    Stack
} from "@mui/material";
import { EmojiEvents } from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import rankingService from "../../services/rankingService";

const COLLEGES = [
    "National Institute of Medical Sciences & Research",
    "Nims Institute of Engineering & Technology",
    "Nims School of Business",
    "Nims School of Law",
    "Nims Institute of Hotel Management",
    "Nims Dental College & Hospital",
    "Nims Institute of Pharmacy",
    "Nims School of Nursing",
    "Nims College of Physiotherapy & Occupational Therapy",
    "Nims College of Allied & Health Care Sciences (Paramedical)",
    "Nims Institute of Humanities & Liberal Arts",
    "Nims Institute of Public Health and Governance",
    "Nims Institute of Fashion Technology",
    "Nims Institute of Aviation",
    "Nims School of Forensic Sciences",
    "Marik Institute of Computing, AI, Robotics & Cybernetics",
    "Nims Institute of Travel & Tourism Management"
];

export default function RankingPage() {
    const { userProfile } = useAuth();
    const [ranking, setRanking] = useState([]);
    const [userRank, setUserRank] = useState(null);
    const [loading, setLoading] = useState(true);
    const [collegeFilter, setCollegeFilter] = useState("");

    useEffect(() => {
        const fetchRanking = async () => {
            try {
                setLoading(true);
                let data;

                if (collegeFilter) {
                    data = await rankingService.getRankingByCollege(collegeFilter);
                } else {
                    data = await rankingService.getGlobalRanking();
                }

                setRanking(data);

                if (userProfile?.uid) {
                    let rank;
                    if (collegeFilter) {
                        rank = await rankingService.getUserRankInCollege(userProfile.uid, collegeFilter);
                    } else {
                        rank = await rankingService.getUserRank(userProfile.uid);
                    }
                    setUserRank(rank);
                }
            } catch (error) {
                console.error("Error fetching ranking:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchRanking();
    }, [userProfile, collegeFilter]);

    const getRankMedalColor = (rank) => {
        if (rank === 1) return "#FFD700";
        if (rank === 2) return "#C0C0C0";
        if (rank === 3) return "#CD7F32";
        return "transparent";
    };

    if (loading) {
        return (
            <AdminLayout title="Ranking">
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Ranking & Leaderboard">
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                    <EmojiEvents sx={{ color: "warning.main" }} />
                    Leaderboard
                </Typography>

                <Card sx={{ mb: 3, backgroundColor: "primary.lighter" }}>
                    <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                            YOUR RANK
                        </Typography>
                        {userRank ? (
                            <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        #{userRank.rank}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {collegeFilter ? "In College" : "Global"}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        {userRank.totalCredits}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Total Credits
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        {Math.round(userRank.percentile)}%
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Percentile
                                    </Typography>
                                </Box>
                            </Stack>
                        ) : (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                No ranking data available yet
                            </Alert>
                        )}
                    </CardContent>
                </Card>

                <Box sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 260 }}>
                        <InputLabel>Filter by College</InputLabel>
                        <Select
                            value={collegeFilter}
                            label="Filter by College"
                            onChange={(e) => setCollegeFilter(e.target.value)}
                        >
                            <MenuItem value="">Global Leaderboard</MenuItem>
                            {COLLEGES.map((college) => (
                                <MenuItem key={college} value={college}>
                                    {college}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {ranking.length === 0 ? (
                    <Alert severity="info">No students in ranking yet</Alert>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead sx={{ backgroundColor: "primary.lighter" }}>
                                <TableRow>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                                        Rank
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        College
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        Credits
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ranking.map((student) => (
                                    <TableRow
                                        key={student.id}
                                        hover
                                        sx={{
                                            backgroundColor:
                                                student.id === userProfile?.uid
                                                    ? "action.selected"
                                                    : student.rank <= 3
                                                        ? "success.lighter"
                                                        : "inherit"
                                        }}
                                    >
                                        <TableCell align="center">
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                {student.rank <= 3 ? (
                                                    <EmojiEvents sx={{ color: getRankMedalColor(student.rank) }} />
                                                ) : (
                                                    <Typography sx={{ fontWeight: 700 }}>{student.rank}</Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Avatar sx={{ width: 32, height: 32, backgroundColor: "primary.main" }}>
                                                    {student.firstName?.charAt(0) || "S"}
                                                </Avatar>
                                                <Typography>
                                                    {student.firstName} {student.lastName}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="caption">{student.collegeName || "-"}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip label={`${student.totalCredits} pts`} color="primary" size="small" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </AdminLayout>
    );
}
